import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../../utils/axiosConfig";
import { EmailValidator } from "../../../../helpers/EmailValidator";
import { ITasksMappings } from "../../../../interfaces/IEmailAlerts";

const useMappings = () => {
    const [tasks, setTasks] = useState<ITasksMappings | undefined>(undefined);
    const [files, setFiles] = useState<string[] | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);

    useEffect(() => {
        getTasks();
    }, [])

    const getTasks = async () => {
        setIsLoading(true);

        try {
            const url = `emails/tasks`;
            const response = await axiosInstance.get(url);

            if (!response.data) {
                throw "Response payload was empty"
            } else {
                setTasks(response.data);
                setIsError(false);
            }
        } catch (error) {
            toast.error("An error occurred when getting the available mappings")
            console.error("Error fetching mappings:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const getFiles = async (task: string) => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get("emails/files", { params: { task } });
            if (!response.data) {
                throw new Error("Response payload was empty");
            } else {
                setFiles(response.data);
                setIsError(false);
            }
        } catch (error) {
            toast.error("An error occurred when getting the available files");
            console.error("Error fetching files:", error);
        } finally {
            setIsLoading(false);
        }
    }
   
    const saveEmail = async (emailAddress: string, task: string, files: string[]) => {
        setIsSaving(true);

        if (!EmailValidator.isValidEmail(emailAddress)) {
            toast.error("A valid email address is required.");
            setIsSaving(false);
            return false;
        }
        
        // For testing allow SFTPtoBlob task to be saved without files
        if (files.length === 0 && task !== "SFTPtoBlob") {
            toast.error("Select a task to receive alerts for.");
            setIsSaving(false);
            return false;
        }

        try {
            await axiosInstance.post("emails", {Address: emailAddress, Task: task, Files: files});
            toast.success("Email address has been saved")
            
            return true;
        } catch (error: any) {
            console.log(error);
            if (error.message.includes("409")) {
                toast.error("Email address already exists");
            } else {
                toast.error("An error ocurred when saving the email");
            }

            setIsError(true)
            
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateEmail = async (emailAddress: string, task: string, files: string[]) => {
        setIsSaving(true);

        if (!EmailValidator.isValidEmail(emailAddress)) {
            toast.error("A valid email address is required.");
            setIsSaving(false);
            return false;
        }

        // For testing allow SFTPtoBlob task to be saved without files
        if (files.length === 0 && task !== "SFTPtoBlob") {
            toast.error("Select a task to receive alerts for.");
            setIsSaving(false);
            return false;
        }

        try {
            await axiosInstance.put("emails", {Address: emailAddress, Task: task, Files: files});
            toast.success("Email address has been updated")
            
            return true;
        } catch (error: any) {
            console.log(error);
            if (error.message.includes("409")) {
                toast.error("Something went wrong");
            } else {
                toast.error("An error ocurred when updating the email");
            }

            setIsError(true)
            
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    return { tasks, files, isLoading, isSaving, isError, saveEmail, getFiles, updateEmail }
}

export default useMappings;